<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useCurrency } from '@composables/useCurrency';
import { trans } from '@elements/translation';
import { currency } from '@elements/number-formatter';
import Panel from '@molecules/components/Panel.vue';
import Button from '@atoms/components/Button.vue';
import { useDataTableStore } from '@stores/useDataTableStore';
import { useSubscriptionStore } from '@organisms/ubiq-administration/store/subscriptions';

const { currencyField } = useCurrency();

const subscriptionStore = useSubscriptionStore();
const datatableStore = useDataTableStore();
const invoice = subscriptionStore.selectedSubscriptionData;

const isMobile = ref(false);
const localTitle = ref(null);
const titleError = ref(null);
const amountError = ref(null);

const price = ref();
const localAmount = currencyField(price);

const emit = defineEmits([ 'close' ]);

const validateTitle = () => {
  titleError.value = localTitle.value && localTitle.value.trim().length > 0
    ? null
    : trans('gestion.invoices.slider.credit_note.field.name.error');
};

const validateAmount = () => {
  if (!localAmount.value || localAmount.value <= 0) {
    amountError.value = trans('gestion.invoices.slider.credit_note.field.amount.error_empty');
    return;
  }

  if (localAmount.value > invoice.amount) {
    amountError.value = trans('gestion.invoices.slider.credit_note.field.amount.error_higher', { amount: currency(invoice.amount) });
    return;
  }

  amountError.value = null;
};

const isValidateDisabled = computed(() => !localTitle.value
  || titleError.value
  || !localAmount.value
  || amountError.value
  || subscriptionStore.isLoading());

// @TODO Centralize in pinia store, see  https://bureaux-a-partager.atlassian.net/browse/MATCH-9839
const checkIsMobile = () => {
  isMobile.value = window.BREAKPOINTS.isMobile;
};

const onValidate = async () => {
  validateTitle();
  validateAmount();

  if (isValidateDisabled.value) {
    return;
  }

  const data = await subscriptionStore.createDiscount(localTitle.value, price.value);

  if (!data?.discount) {
    return;
  }

  datatableStore.addData(data.discount);

  emit('close');
};

onMounted(() => {
  checkIsMobile();
  window.addEventListener('resize', checkIsMobile);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkIsMobile);
});
</script>

<template>
  <div class="slider-credit-note">
    <div class="slider-credit-note__content">
      <div class="slider-credit-note__form">
        <div>
          <label for="credit-note-name">{{ trans('gestion.invoices.slider.credit_note.field.name') }}</label>
          <input
            id="credit-note-name"
            v-model="localTitle"
            :class="{ 'error': titleError }"
            type="text"
            @blur="validateTitle"
            @input="validateTitle"
          >
          <span v-show="titleError" class="error-message">{{ titleError }}</span>
        </div>
        <div>
          <label for="credit-note-amount">{{ trans('gestion.invoices.slider.credit_note.field.amount') }}</label>
          <div class="input-icon">
            <input
              id="credit-note-amount"
              v-model="localAmount"
              :class="{ 'error': amountError }"
              type="number"
              min="0"
              @blur="validateAmount"
              @input="validateAmount"
            >
            <i>
              {{ trans('global.price_tax') }}&nbsp;{{ trans('global.currency_symbol') }}
            </i>
          </div>
          <span v-show="amountError" class="error-message">{{ amountError }}</span>
        </div>
      </div>
      <panel
        type="info"
        class="slider-credit-note__panel"
        :content="trans('gestion.invoices.slider.credit_note.panel', { amount: currency(invoice.amount / 100) })"
        icon="ph_lightbulb-fill_blue"
      />
    </div>
    <div class="slider-credit-note__footer">
      <div class="slider-credit-note__footer-buttons">
        <Button variant="secondary" @click="emit('close')">
          {{ trans('gestion.invoices.slider.credit_note.ctas.cancel') }}
        </Button>
        <Button
          variant="primary"
          :class="{ 'loading-btn': subscriptionStore.isLoading() }"
          :disabled="isValidateDisabled"
          @click="onValidate"
        >
          {{ trans('gestion.invoices.slider.credit_note.ctas.create') }}
        </button>
      </div>
    </div>
  </div>
</template>
